// Display only. Amounts come from the server in minor units and are never totalled here.
export const formatMinor = (minor, currency = "INR") => {
  if (typeof minor !== "number" || !Number.isFinite(minor)) {
    return "-";
  }
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
  }).format(minor / 100);
};

// A change in minor units with an explicit sign, for the draft against live columns.
export const formatMinorDelta = (minor, currency = "INR") => {
  if (typeof minor !== "number" || !Number.isFinite(minor)) {
    return "-";
  }
  if (minor === 0) {
    return "No change";
  }
  return `${minor > 0 ? "+" : "-"}${formatMinor(Math.abs(minor), currency)}`;
};

// 500 -> "5%", 120 -> "1.2%", 8 -> "0.08%". Integer arithmetic, no floating point.
export const formatBps = (bps) => {
  if (typeof bps !== "number" || !Number.isFinite(bps)) {
    return "-";
  }
  const sign = bps < 0 ? "-" : "";
  const abs = Math.abs(Math.trunc(bps));
  const whole = Math.trunc(abs / 100);
  const fraction = String(abs % 100)
    .padStart(2, "0")
    .replace(/0+$/, "");
  return `${sign}${whole}${fraction === "" ? "" : `.${fraction}`}%`;
};

// Dates from the service: an ISO 8601 instant or a plain YYYY-MM-DD. Shown in the browser's
// time zone for instants; a plain date is shown as it is (it has no time zone).
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const formatDate = (value) => {
  if (typeof value !== "string" || value === "") {
    return "-";
  }
  const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (plain) {
    return `${Number(plain[3])} ${MONTHS[Number(plain[2]) - 1] || plain[2]} ${plain[1]}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

export const formatDateTime = (value) => {
  if (typeof value !== "string" || value === "") {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return formatDate(value);
  }
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${formatDate(value)}, ${hours % 12 === 0 ? 12 : hours % 12}:${minutes} ${hours < 12 ? "am" : "pm"}`;
};

// A coupon validity in months, in words: 36 -> "3 years", 18 -> "18 months", 1 -> "1 month".
export const formatValidityMonths = (months) => {
  if (!Number.isInteger(months) || months < 1) {
    return "";
  }
  if (months % 12 === 0) {
    const years = months / 12;
    return `${years} year${years === 1 ? "" : "s"}`;
  }
  return `${months} month${months === 1 ? "" : "s"}`;
};

// A link from the server goes into href or src only when it is plain http or https.
export const safeHttpUrl = (value) => {
  if (typeof value !== "string") {
    return null;
  }
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.href : null;
  } catch (error) {
    return null;
  }
};
